/*****************************************************************
 *  ZOHO BRIDGE PANEL — EVERY WORD ON THE SCREEN                   *
 *  File: apps/web/src/pages/bridge/bridgeWords.js                 *
 *  What it does: holds every label, hint and message the panel    *
 *  shows, in plain English, in one place.                         *
 *  Reads: nothing.   Writes: nothing.                             *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 — THE WORDS                                          *
 *  SECTION 2 — STATUS AND KIND NAMES                              *
 *****************************************************************/

/*****************************************************************
 *  SECTION 1 — THE WORDS                                          *
 *****************************************************************/

export const BRIDGE_WORDS = {
  pageTitle: 'Zoho bridge',
  pageIntro: 'Prices, customers and properties copied from Zoho wait here. Nothing goes into '
    + 'Butler until you approve it, and nothing is ever sent back to Zoho.',
  notAllowed: 'This page is for owners and admins. Ask an owner to change your role on the HR Team page.',
  loading: 'Loading…',
  loadFailed: 'Could not load this. Check your connection and try again.',
  tryAgain: 'Try again',

  tabInbox: 'Waiting',
  tabProperties: 'Properties',
  tabPrices: 'Prices',
  tabCustomers: 'Customers',
  tabKeys: 'Keys',
  tabLog: 'Log',

  lastHeard: 'Last delivery from Zoho',
  neverHeard: 'No delivery yet',

  // ---- the health strip (defects 378, 392, 393) ----
  healthTitle: 'Is the bridge working?',
  lastKnock: 'Bridge last checked in',
  neverKnocked: 'The bridge has never checked in',
  lastNewRecord: 'Newest record arrived',
  bridgeOk: 'Working',
  bridgeQuiet: 'Not heard from in a while',
  bridgeQuietWhy: 'The bridge checks in every 15 minutes. Nothing for 45 minutes or more '
    + 'means it may have stopped. Zoho being quiet does not cause this.',
  lastRefusalWord: 'Turned away',
  keyWorkingCount: 'working key(s)',
  noWorkingKey: 'No working key: the bridge cannot get in. Make one on the Keys tab.',
  keyExpiresIn: 'The bridge key stops working in',
  days: 'days',

  // ---- the Zoho field pack (phase 2b) ----
  fieldPackTitle: 'Zoho fields in Customer Butler',
  fieldPackReady: 'Ready: every Zoho field has a place in Customer Butler.',
  fieldPackMissing: 'Not set up yet. The first approval sets them up, or do it now.',
  fieldPackButton: 'Set up the Zoho fields now',
  fieldPackDone: 'Done. Customer Butler now holds the Zoho fields.',

  filterStatus: 'Show',
  filterKind: 'Type',
  filterAllKinds: 'Everything',
  filterBadge: 'Fields',
  filterAnyBadge: 'Full and thin',
  filterSearch: 'Name or Zoho id',
  emptyPile: 'Nothing here. When the bridge delivers from Zoho, it shows up here.',
  selectAll: 'Select all on this page',
  approveSelected: 'Approve selected',
  selectedCount: 'selected',
  approveAllThatMatch: 'Approve all that match',
  approveAllConfirm: 'Approve all',
  approveAllExplain: 'This approves every record that matches these filters, on every page, '
    + 'one by one, the same as tapping Approve on each. Customers go first, then properties.',
  approveAllRunning: 'Approving',
  approveAllFinished: 'Finished',
  approveAllStopped: 'Stopped',
  approveAllStop: 'Stop after this one',
  of: 'of',

  // ---- paging (defect 372) ----
  pagePrevious: 'Previous page',
  pageNext: 'Next page',
  showing: 'Showing',
  pageWord: 'Page',

  // ---- full fields and properties on a card (phase 2b) ----
  fullFields: 'Full fields',
  thinFields: 'Thin: name, email and phone only',
  allZohoFields: 'Every Zoho field',
  hideZohoFields: 'Hide the Zoho fields',
  zohoFieldsCount: 'Zoho fields filled in',
  notInMap: 'Not in the field map (kept in the holding pile)',
  goesNowhere: 'not copied',
  copiedEarlier: 'earlier copies kept',
  linkedContactsTitle: 'Zoho contacts on this property',
  inButlerAs: 'in Butler as',
  notInButlerYet: 'not in Butler yet',
  noContactsOnProperty: 'Zoho has no contact on this property.',
  waitingBecause: 'Waiting:',
  chooseCustomer: 'Pick the right customer',
  candidatesTitle: 'These Butler customers fit. Pick one, or make a new customer.',
  useThisCustomer: 'Use this customer',
  makeNewCustomer: 'Make a new customer instead',
  chooseOwner: 'Pick the customer this property sits under',
  fieldWarnings: 'Kept as sent, with a note',
  followUpPutIn: 'properties that were waiting went in too',

  whyLabel: 'Why? (kept with the decision)',
  whyPlaceholder: 'e.g. checked against the price sheet',
  approve: 'Approve',
  reject: 'Reject',
  cancel: 'Cancel',
  confirmApprove: 'Approve and write into Butler',
  confirmReject: 'Reject (kept, nothing written)',
  useOlderAnyway: 'Use this older version anyway',

  whatZohoSays: 'What Zoho says',
  whatChanges: 'What would change in Butler',
  whatChanged: 'What changed in Butler',
  noChanges: 'Nothing would change.',
  brandNew: 'New: Butler does not have this yet.',
  wasBrandNew: 'New: this was not in Butler before.',
  linkedToCustomer: 'Linked to Butler customer',
  looksLike: 'Looks like Butler customer',
  matchedBy: 'matched by',
  noMatch: 'No Butler customer matches. Approving makes a new one.',
  willUpdate: 'Approving fills this customer in from Zoho. Blank Zoho fields leave Butler values alone.',
  decidedBy: 'Decided',
  failedBecause: 'Could not apply:',
  receivedAt: 'Arrived',

  pricesEmpty: 'No prices approved yet.',
  priceHistory: 'Price history',
  closeHistory: 'Close',
  historyEmpty: 'No changes recorded.',

  linksEmpty: 'No customers linked yet.',
  linkedTo: 'Linked to',
  zohoContact: 'Zoho contact',
  relink: 'Link to a different customer',
  searchCustomers: 'Search Butler customers by name, email or phone',
  search: 'Search',
  chooseThis: 'Link to this one',
  showOldLinks: 'Show replaced links too',
  replaced: 'Replaced',

  keysIntro: 'A key lets the bridge robot drop records in the waiting pile. '
    + 'A key can never approve anything.',
  keyName: 'Key name',
  keyNamePlaceholder: 'e.g. Zoho bridge on the bot box',
  makeKey: 'Make a key',
  keyShownOnce: 'Copy this key now. It will never be shown again.',
  copyKey: 'Copy key',
  copied: 'Copied',
  copyFailed: 'Could not copy. Select the text and copy it by hand.',
  keysEmpty: 'No keys yet.',
  lastUsed: 'Last used',
  neverUsed: 'Never used',
  revoke: 'Switch off',
  revokeWhy: 'Why switch it off?',
  confirmRevoke: 'Switch this key off',
  keyOff: 'Switched off',
  keyOn: 'Working',
  keyExpired: 'Expired: the door turns it away',
  expires: 'Expires',

  logEmpty: 'Nothing logged yet.',

  // ---- phase 2a (2026-10-13): visits from Zoho and the technician match ----
  tabVisits: 'Visits',
  tabTechs: 'Technicians',
  visitsIntro: 'Technicians\' Zoho meetings that are linked to a job wait here. Approving one puts it on '
    + 'that technician\'s Dispatch calendar. Change a visit in Zoho; Butler copies the change.',
  visitsEmpty: 'Nothing here. When the bridge copies meetings from Zoho, they show up here.',
  visitsShow: 'Show',
  visitsWaiting: 'Waiting for you',
  visitsApplied: 'On the calendar',
  visitsCancelled: 'Cancelled in Zoho',
  visitsSetAside: 'Set aside',
  modeLabel: 'How visits reach the calendar',
  modeApproveEach: 'Approve each one (trial)',
  modeAutomatic: 'Automatic for confirmed technicians',
  modeUnknown: 'Dispatch did not say',
  modeChangeIt: 'Change it on the Dispatch panel',
  revealLabel: 'What a tech sees of his day',
  revealCurrentOnly: 'One job at a time',
  revealCurrentPlusCount: 'One job at a time, plus how many are left',
  revealFullDay: 'The whole day',
  notConnected: 'Dispatch is not connected to this company, so visits cannot be placed here.',
  visitWhen: 'When',
  visitTech: 'Technician',
  visitHelpers: 'Riding along',
  visitAddress: 'Address',
  visitCustomer: 'Customer',
  visitService: 'Service code in Zoho',
  visitJob: 'Zoho job',
  visitNotes: 'Notes from Zoho',
  visitBookedBy: 'Booked in Zoho by',
  visitDeletedBy: 'Deleted in Zoho by',
  visitWas: 'Was',
  visitPickup: 'Pick-up',
  techNotMatched: 'not matched yet',
  techWaitingForYou: 'match waiting for you',
  techSaysNot: 'not on the roster',
  confirmVisit: 'Approve and put on the calendar',
  confirmCancel: 'Approve the cancel (the visit is kept, marked cancelled)',
  rulesBroken: 'Dispatch rules this booking breaks (it is on the calendar anyway)',
  goodToKnow: 'Good to know',
  openInDispatch: 'Open in Dispatch',
  leftOff: 'Left off (not confirmed)',
  cannotLandYet: 'Why it cannot land yet',

  techsIntro: 'Each Zoho person who hosts or rides along on a meeting is matched once to a person on the '
    + 'Dispatch roster. The bridge only suggests; you confirm. A visit waits until its technician is confirmed.',
  techsEmpty: 'No Zoho people seen yet. They appear when the bridge copies their first meeting.',
  techsZohoPerson: 'In Zoho',
  techsButlerPerson: 'In Butler (Dispatch roster)',
  techsPick: 'Pick a person',
  techsSuggested: 'Suggested',
  techsConfirm: 'Confirm this match',
  techsChange: 'Change',
  techsNotOnRoster: 'Not on the roster',
  techsAskAgain: 'Suggest again',
  techsShowOld: 'Show replaced matches too',
  techsRosterProblem: 'Could not read the Dispatch roster',
  techsAddOnHr: 'Someone missing? Add them on the HR Team page, then press Suggest again.',
  techsConfirmWhy: 'Why? (e.g. same person, Zoho uses an older email)',
  techsUnmatchWhy: 'Why? (e.g. lab only, never goes to the field)',
  techsDecided: 'Decided',
  techsVisitsWaiting: 'visits waiting',
};

/*****************************************************************
 *  SECTION 2 — STATUS AND KIND NAMES                              *
 *****************************************************************/

// The word a person sees for each status (never the system word alone).
export const STATUS_NAMES = {
  proposed: 'Waiting for you',
  approved: 'Being applied',
  applied: 'In Butler',
  rejected: 'Rejected',
  failed: 'Needs a person',
  waiting: 'Approved, waiting for its customer',
  superseded: 'Replaced by a newer copy',
  all: 'Everything',
};

// The word a person sees for each kind of record.
export const KIND_NAMES = {
  books_item: 'Price',
  crm_contact: 'Customer',
  crm_property: 'Property',
  visit: 'Visit',
};

// Phase 2a: what a visit card says it is.
export const VISIT_CHANGE_NAMES = {
  new: 'New visit',
  moved: 'Moved in Zoho',
  changed: 'Changed in Zoho',
  cancel: 'Cancelled in Zoho',
};

// Phase 2a: where a technician match is, in words.
export const TECH_STATUS_NAMES = {
  proposed: 'Waiting for you',
  confirmed: 'Confirmed',
  unmatched: 'Not on the roster',
  none: 'Not seen yet',
};

// Phase 2a: how a technician match was suggested.
export const TECH_HOW_NAMES = {
  email: 'the same email',
  name: 'the same name',
  name_initial: 'the same first name and last initial',
  manual: 'picked by hand',
  none: 'nothing on the roster looks like them',
};

// Phase 2a: the office's flags in Zoho titles, in words.
export const FLAG_NAMES = {
  'CA!': 'Call ahead',
  'CA!!': 'Call ahead',
  'NOTES!': 'Read the notes',
  'AM!': 'Morning',
  'SAME DAY report!': 'Same-day report',
};

// How a match was made, in words.
export const MATCH_NAMES = {
  zohoId: 'an earlier link',
  email: 'email',
  phone: 'phone only (check it is the same person)',
  none: 'nothing',
  ambiguous: 'two different customers',
  manual: 'a person, by hand',
  chosen: 'a person, while approving',
  madeNew: 'a person asked for a new customer',
};

// The log's action words, in plain English.
export const LOG_ACTION_NAMES = {
  'inbox.receive': 'Delivery from Zoho',
  'inbox.approve': 'Approved',
  'inbox.reject': 'Rejected',
  'link.relink': 'Relinked a customer',
  'key.issue': 'Key made',
  'key.revoke': 'Key switched off',
  'tech.confirm': 'Confirmed a technician match',
  'tech.unmatch': 'Marked "not on the roster"',
  'tech.suggest': 'Asked the roster again',
  'visit.automatic': 'Visit placed automatically',
  no_key: 'Knock with no key',
  bad_key: 'Knock with a bad key',
  wrong_scope: 'Key used on the wrong door',
  switched_off_key: 'Knock with a switched-off key',
  expired_key: 'Knock with an expired key',
  'inbox.continue': 'A waiting property went in',
  'fieldpack.ensure': 'Zoho fields set up in Customer Butler',
  'job.approveAll.start': 'Approve all that match: started',
  'job.approveAll.finished': 'Approve all that match: finished',
  'job.approveAll.stopped': 'Approve all that match: stopped',
  'job.approveAll.stopAsked': 'Approve all that match: stop asked',
};
